<script>
  import { afterUpdate, getContext, onDestroy, onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { link, useNavigate } from 'svelte-navigator';
  import {
    Breadcrumb,
    BreadcrumbItem,
    Button,
    Col,
    Container,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Row,
    Tooltip
  } from '@sveltestrap/sveltestrap';

  import { BrandWizard } from '@client/components/brand-wizard';
  import { EmptyState } from '@client/components/empty-state';
  import { FilterBar } from '@client/components/filter-bar';
  import { SearchFilter } from '@client/components/filters/search';
  import { SortFilter } from '@client/components/filters/sort';
  import { ToggleFilter } from '@client/components/filters/toggle';
  import { Icon } from '@client/components/icon';
  import { Loader } from '@client/components/loader';
  import { NoMatches } from '@client/components/no-matches';
  import { getNotificationsContext } from '@client/components/notifications';
  import { Pagination } from '@client/components/pagination';
  import { RenameModal } from '@client/components/rename-modal';
  import { Spinner } from '@client/components/spinner';
  import { Tour } from '@client/components/tour';

  import { DomainsService } from '@client/services/domains';
  import { GroupsService } from '@client/services/groups';
  import { SignaturesService } from '@client/services/signatures';
  import { TagBlocksService } from '@client/services/tag-blocks';

  import { hasBranding } from '@client/stores/brand';
  import { isOutlookVisualPreview } from '@client/stores/domain-settings';
  import { studioSettingsStore } from '@client/stores/page-settings';
  import { loadStudioTemplatePreviews } from '@client/stores/signature-templates';
  import { userStore } from '@client/stores/user';

  import { QueryParams } from '@client/utils/query-params';
  import { generateUniqueRulename } from '@client/utils/rulenames';
  import { getAllSignatureWarnings } from '@client/utils/signatures';
  import { setTourStoreSettings } from '@client/utils/tours';

  import { SIGNATURE_WARNINGS, STUDIO_FILTERS } from '@client/enums';

  import { sortCollection } from '@common/utils/arrays';
  import { debounce } from '@common/utils/debounce';
  import { merge } from '@common/utils/objects';

  import { CLONE_STEPS } from './tours/clone';
  import { CREATE_STEPS } from './tours/create';
  import { EDIT_STEPS } from './tours/edit';
  import { FULL_STEPS } from './tours/full';
  import { OVERVIEW_STEPS } from './tours/overview';
  import { ChoiceModal, CloneModal, GalleryModal } from './modals';

  const { addNotification } = getNotificationsContext();
  const { basepath } = getContext('config');

  const navigate = useNavigate();
  const redirect = (path) => navigate(path);

  // Cap how many issue items a warning tooltip lists before collapsing the rest into "+ N more".
  const MAX_TOOLTIP_ITEMS = 5;

  const pageQueryParam = parseInt(QueryParams.get('page'), 10);
  const perPageQueryParam = parseInt(QueryParams.get('perpage'), 10);
  const includeArchived = QueryParams.get('includeArchived');

  let filters = merge({}, { ...STUDIO_FILTERS }, $studioSettingsStore.filters);

  Object.keys(filters.sorting.options).forEach((key) => {
    filters.sorting.options[key].label = STUDIO_FILTERS.sorting.options[key].label;
  });

  if (!filters.search) {
    filters.search = QueryParams.get('search') || '';
  }

  if (includeArchived) {
    filters.archived = includeArchived;
  }

  if (pageQueryParam && pageQueryParam !== filters.paging.page) {
    filters.paging.page = pageQueryParam;
  }

  if (pageQueryParam && perPageQueryParam !== filters.paging.perPage) {
    filters.paging.perPage = perPageQueryParam;
  }

  let showFilters = true;
  let queryResult = [];
  let records = [];
  let activeSignatures = [];
  let otherSignatures = [];
  let totalRecords = 0;
  let previewTemplates = [];
  let updating = false;

  let showSignatureChoiceModal = false;
  let showSignatureTemplatesModal = false;
  let showCloneToDomainModal = false;
  let showRenameModal = false;
  let showBrandWizardModal = false;

  let renameSignature = null;
  let cloneSignature = {};
  let cloneSignatureRulename = '';
  let domains = [];
  let groups = [];
  let initialized = false;
  let originalRecordCount = 0;
  let inflightRequests = [];

  const onError = (text) => {
    addNotification({
      text,
      type: 'error',
      autohide: 2500,
      position: 'top-right'
    });
  };

  const updateQueryParams = (filters) => {
    $studioSettingsStore.filters = { ...filters };
  };

  const onNewSignature = (e) => {
    e.preventDefault();

    showSignatureChoiceModal = true;
  };

  const openTemplateGallery = async () => {
    showSignatureChoiceModal = false;
    // Clear any previous open's templates so the gallery can't show/seed a stale list
    // while the fresh previews load (the Use button is gated on previewTemplates.length).
    previewTemplates = [];

    setTimeout(() => (showSignatureTemplatesModal = true), 200);

    try {
      previewTemplates = await loadStudioTemplatePreviews();
    } catch (error) {
      onError($format('label.FAILURE.LOAD_SIGNATURE_TEMPLATES'));
    }
  };

  const onGoBack = () => {
    showSignatureTemplatesModal = false;
    setTimeout(() => (showSignatureChoiceModal = true), 200);
  };

  const getUserSignature = async (signature) => {
    let email = 'adminuser'; // $userStore.currentUser.email;

    if (signature.html) {
      return signature.html;
    }

    const sendergroups = Array.isArray(groups)
      ? groups.filter(({ groupname }) => signature.sendergroups.some((group) => groupname === group.groupname))
      : [];

    let getSender = sendergroups.length && sendergroups[0].senders.length;

    if (getSender) {
      const username = sendergroups[0].senders[0].username || email;

      if (username !== '*') {
        email = username;
      }
    }

    const data = {
      username: email,
      scratchblockname: signature.rulename,
      testData: {
        testsender: email
      }
    };

    signature.loading = true;

    const { rendered } = await SignaturesService.getSignature(data);

    signature.loading = false;
    signature.html = rendered;

    return rendered;
  };

  const onSearchFilterChange = debounce(() => {
    filters.paging.page = 1;
    update({ type: 'filter' });
  }, 250);

  const onSortChange = (selected, key, dir) => {
    filters.sorting.key = key;
    filters.sorting.dir = dir;

    update(true);
  };

  const onArchiveChange = (value) => {
    filters.archived = value;
    filters.paging.page = 1;

    update(true);
  };

  const onChangePage = (page) => {
    filters.paging.page = page;
    update(true);
  };

  const onChangeRecords = (perPage) => {
    filters.paging.perPage = perPage;
    update(true);
  };

  const onCloneSignatureToDomain = (signature) => {
    cloneSignature = signature;
    cloneSignatureRulename = signature.rulename;
    showCloneToDomainModal = true;
  };

  const onRenameSignature = (signature) => {
    renameSignature = signature;
    showRenameModal = true;
  };

  const onRenameUpdate = async (signature, newName) => {
    const response = await TagBlocksService.save({ ...signature, rulename: newName });

    if (response.status >= 400) {
      return onError($format('label.FAILURE.RENAME_SIGNATURE', { values: { rulename: signature.rulename } }));
    }

    addNotification({
      text: $format('label.SUCCESS.RENAME', { values: { from: signature.rulename, to: newName } }),
      type: 'success',
      autohide: 2500,
      position: 'top-right'
    });

    showRenameModal = false;
    update(false);
  };

  const onCloneSignature = async (signature) => {
    updating = true;

    const rulename = generateUniqueRulename(signature.rulename);

    const data = {
      rulename,
      htmltemplate: signature.htmltemplate,
      texttemplate: signature.texttemplate,
      replyhtmltemplate: signature.replyhtmltemplate,
      replytexttemplate: signature.replytexttemplate,
      replytemplateenabled: signature.replytemplateenabled,
      params: signature.params
    };

    const response = await TagBlocksService.create(data);

    updating = false;

    if (response.status === 500) {
      updating = false;
      return onError($format('label.FAILURE.SIGNATURE_CLONE', { values: { rulename: signature.rulename } }));
    }

    addNotification({
      text: $format('label.SUCCESS.CLONE', { values: { type: signature.rulename } }),
      type: 'success',
      autohide: 2500,
      position: 'top-right'
    });

    setTimeout(() => redirect(`/signatures/studio/${response.rulename}`), 200);
  };

  const onArchiveSignature = async (signature) => {
    updating = true;

    const response = await TagBlocksService.save({ ...signature, archived: !signature.archived });

    if (response.status >= 400) {
      updating = false;
      return onError($format('label.FAILURE.ARCHIVE_SIGNATURE', { values: { rulename: signature.rulename } }));
    }

    addNotification({
      text: $format('label.SUCCESS.ARCHIVE', { values: { type: signature.rulename } }),
      type: 'success',
      autohide: 2500,
      position: 'top-right'
    });

    records.splice(
      records.findIndex((item) => item.rulename === signature.rulename),
      1,
      { ...signature, archived: response.archived }
    );
    otherSignatures.splice(
      otherSignatures.findIndex((item) => item.rulename === signature.rulename),
      1,
      { ...signature, archived: response.archived }
    );

    otherSignatures = [...otherSignatures];
    records = [...records];

    update(true);
  };

  const onDeleteSignature = async (signature) => {
    const confirmed = confirm(
      $format('label.DELETE_PROMPT', { values: { type: $format('label.SIGNATURE').toLowerCase() } })
    );

    if (confirmed) {
      updating = true;

      const response = await TagBlocksService.delete(signature);

      if (response.status >= 400) {
        updating = false;
        return onError($format('label.FAILURE.DELETE_SIGNATURE', { values: { rulename: signature.rulename } }));
      }

      addNotification({
        text: $format('label.SUCCESS.DELETE', { values: { type: signature.rulename } }),
        type: 'success',
        autohide: 2500,
        position: 'top-right'
      });

      records.splice(
        records.findIndex((item) => item.rulename === signature.rulename),
        1
      );
      otherSignatures.splice(
        otherSignatures.findIndex((item) => item.rulename === signature.rulename),
        1
      );

      otherSignatures = [...otherSignatures];
      records = [...records];

      if (!records.length && filters.paging.page > 1) {
        filters.paging.page -= 1;
      }

      if (!otherSignatures.length && !records.length) {
        originalRecordCount = 0;
      }

      update(true);
    }
  };

  const trackRequest = (request, label) => {
    if (request?.cancel) {
      inflightRequests = [...inflightRequests, { request, label }];
    }

    return request;
  };

  const cancelInflightRequests = (reason) => {
    if (!inflightRequests.length) {
      return;
    }

    inflightRequests.forEach(({ request, label }) => {
      if (request?.cancel) {
        request.cancel();
        console.info('Request cancelled', { label, reason, route: 'signatures/studio' });
      }
    });

    inflightRequests = [];
  };

  const update = async (skip) => {
    cancelInflightRequests('update-restart');
    updating = true;

    if (!skip) {
      activeSignatures = [];
      otherSignatures = [];

      const blocksRequest = trackRequest(TagBlocksService.getAll(), 'tagblocks:getAll');
      const signaturesRequest = trackRequest(SignaturesService.getAll(), 'signatures:getAll');

      try {
        const [blocks, signatures] = await Promise.all([blocksRequest, signaturesRequest]);

        if (!Array.isArray(blocks) || !Array.isArray(signatures)) {
          activeSignatures = [];
          otherSignatures = [];
          updating = false;
          inflightRequests = [];
          return;
        }

        const ids = signatures.map(({ blockname, status }) => status && blockname).filter(Boolean);

        if (!initialized) {
          originalRecordCount = blocks.length;
        }

        blocks.forEach((item, index) => {
          item.order = index;
          item.sendergroups = signatures.find(({ blockname }) => blockname === item.rulename)?.sendergroups || [];
          item.active = ids.includes(item.rulename);

          if (item.active) {
            activeSignatures.push(item);
          } else {
            otherSignatures.push(item);
          }
        });
      } catch (error) {
        if (error?.message === 'Canceled' || error?.__CANCEL__) {
          updating = false;
          return;
        }

        throw error;
      } finally {
        inflightRequests = [];
      }
    }

    queryResult = [...activeSignatures, ...otherSignatures];
    records = [...queryResult];

    if (!filters.archived) {
      records = records.filter((item) => !item.archived);
      queryResult = queryResult.filter((item) => !item.archived);
    }

    if (filters.search.length) {
      records = queryResult.filter(
        (item) => JSON.stringify(Object.values(item).join(',')).toLowerCase().indexOf(filters.search.toLowerCase()) > -1
      );
    }

    const recordCount = records.length;
    const sortDirection = filters.sorting.dir === 'asc' ? -1 : 1;

    records = sortCollection(records, filters.sorting.key, sortDirection).slice(
      (filters.paging.page - 1) * filters.paging.perPage,
      filters.paging.page * filters.paging.perPage
    );

    totalRecords = filters.search.length ? recordCount : queryResult.length;

    initialized = true;

    updating = false;
  };

  const documentKeyUp = (e) => {
    if (e.code === 27 && (showSignatureChoiceModal || showSignatureTemplatesModal || showCloneToDomainModal)) {
      showSignatureChoiceModal = false;
      showSignatureTemplatesModal = false;
      showCloneToDomainModal = false;
    }
  };

  const getDomainsList = async () => {
    const response = await DomainsService.getAll({ screen: $userStore.isStaff ? 'staff' : '', perPage: 250 });
    const data = Array.isArray(response?.data) ? response.data : [];
    domains = data.map(({ domainname }) => ({ value: domainname, label: domainname }));
  };

  const getGroupsList = async () => {
    const response = await GroupsService.getAll();
    groups = Array.isArray(response) ? response : [];
  };

  onMount(
    () => {
      getGroupsList();
      updateQueryParams(filters);
      getDomainsList();
      update();

      document.body.addEventListener('keyup', documentKeyUp);
    },
    () => {
      document.body.removeEventListener('keyup', documentKeyUp);
    }
  );

  afterUpdate(() => {
    $studioSettingsStore = setTourStoreSettings($studioSettingsStore, !!records?.length);
  });

  onDestroy(() => cancelInflightRequests('component-destroyed'));

  $: hasData = $studioSettingsStore?.tours?.hasData;

  $: studioTourSteps = [
    {
      steps: OVERVIEW_STEPS,
      stepsKey: 'overview',
      menuLabel: $format('label.TOURS.STUDIO.MENU.OVERVIEW'),
      storeKey: 'overviewHasToured',
      disabled: !hasData
    },
    {
      steps: CREATE_STEPS,
      stepsKey: 'default',
      menuLabel: $format('label.TOURS.STUDIO.MENU.CREATE'),
      storeKey: 'createHasToured',
      disabled: false
    },
    {
      steps: CLONE_STEPS,
      stepsKey: 'clone',
      menuLabel: $format('label.TOURS.STUDIO.MENU.CLONE'),
      storeKey: 'cloneHasToured',
      disabled: !hasData
    },
    {
      steps: EDIT_STEPS,
      stepsKey: 'edit',
      menuLabel: $format('label.TOURS.STUDIO.MENU.EDIT'),
      storeKey: 'editHasToured',
      disabled: !hasData
    },
    {
      steps: FULL_STEPS,
      stepsKey: 'full'
    }
  ];

  $: dismissedOrToured = $studioSettingsStore?.tours?.isDismissed || $studioSettingsStore?.tours?.hasToured;
  $: hasSignatures = initialized && originalRecordCount > 0;
  $: updateQueryParams(filters);
</script>

<div id="studio" class="page">
  <Loader show={updating} backdrop={true} />
  <div class="page-header">
    <Container fluid>
      <Row>
        <Col md="12">
          <Breadcrumb>
            <BreadcrumbItem>
              <a href="{basepath}/signatures/studio" use:link>{$format('label.SIGNATURES')}</a>
            </BreadcrumbItem>
            <BreadcrumbItem active>{$format('label.DESIGNER_STUDIO')}</BreadcrumbItem>
          </Breadcrumb>
        </Col>
      </Row>
      <Row class="flex-align--center">
        <Col xs="6" sm="5" md="7">
          <h1 class="page-title">{$format('label.DESIGNER_STUDIO')}</h1>
        </Col>
        <Col xs="6" sm="7" md="5" class="text-right">
          {#if dismissedOrToured && hasSignatures}
            <Tour steps={studioTourSteps} store={studioSettingsStore} />
          {/if}
          <Button
            id="setup-branding"
            class="me-2 hide--mobile"
            color="secondary"
            on:click={() => (showBrandWizardModal = true)}
          >
            {$format($hasBranding ? 'label.UPDATE_BRANDING' : 'label.SETUP_BRANDING')}
          </Button>
          <Button id="add-signature" color="secondary" data-testid="add-signature" on:click={onNewSignature}>
            {$format('label.NEW_SIGNATURE')}
          </Button>
        </Col>
      </Row>
    </Container>
  </div>
  <div class="page-content">
    {#if !dismissedOrToured && hasSignatures}
      <Tour
        steps={studioTourSteps}
        store={studioSettingsStore}
        tourHeader={$format('label.TOURS.STUDIO.ALERT.HEADER')}
        tourDescription={$format('label.TOURS.STUDIO.ALERT.DESCRIPTION')}
        marginBottom="mb-0"
        marginTop="mt-3"
      />
    {/if}
    {#if hasSignatures}
      <div class="filters">
        <FilterBar bind:show={showFilters}>
          <SearchFilter bind:search={filters.search} onChange={onSearchFilterChange} />
          <div class="right-filters">
            <ToggleFilter
              label={$format('label.INCLUDE_ARCHIVED')}
              active={filters.archived}
              param="includeArchived"
              onClick={onArchiveChange}
            />
            <SortFilter
              bind:filter={filters.sorting.options}
              bind:sortDirection={filters.sorting.dir}
              onClick={onSortChange}
              sortDirParam="sortDir"
              sortKeyParam="sortKey"
            />
          </div>
        </FilterBar>
      </div>
    {/if}
    <Container fluid>
      {#if hasSignatures}
        {#if records.length}
          <div class="signature-cards">
            {#each records as signature (signature._id)}
              <div
                class="signature-card"
                on:click={() => redirect(signature.rulename)}
                role="button"
                tabindex="0"
                data-testid="sig-card-{signature.rulename}"
              >
                <div class="signature-card--header">
                  <div class="signature-card--rule">
                    {signature.rulename}

                    {#if signature.active}
                      <div class="signature-card--status">{$format('label.ROUTING')}</div>
                    {/if}

                    {#if signature.archived}
                      <div class="signature-card--archived">{$format('label.ARCHIVED')}</div>
                    {/if}
                  </div>

                  <div class="warnings" on:click|preventDefault|stopPropagation role="button" tabindex="0">
                    {#each getAllSignatureWarnings( signature, $userStore?.cname[1], [$userStore.domain], [], { isOutlookVisualPreview: $isOutlookVisualPreview } ) as { type, data }}
                      <div
                        class="warning"
                        id="warnings-{type}-{signature._id}"
                        style="border-color: {SIGNATURE_WARNINGS[type]?.color}; color: {SIGNATURE_WARNINGS[type]
                          ?.color};"
                      >
                        <Icon name={SIGNATURE_WARNINGS[type]?.icon} size={SIGNATURE_WARNINGS[type]?.iconSize} />
                      </div>
                      <Tooltip class="warnings-tooltip" target="warnings-{type}-{signature._id}">
                        <div class="warning-tooltip-content">
                          <b>{$format(SIGNATURE_WARNINGS[type]?.label)} ({data.length})</b>
                          <ul>
                            {#each data.slice(0, MAX_TOOLTIP_ITEMS) as item}
                              <li>{item}</li>
                            {/each}
                            {#if data.length > MAX_TOOLTIP_ITEMS}
                              <li>+ {data.length - MAX_TOOLTIP_ITEMS} {$format('label.MORE')}</li>
                            {/if}
                          </ul>
                        </div>
                      </Tooltip>
                    {/each}
                  </div>

                  <div
                    class="signature-card--actions"
                    on:click|preventDefault|stopPropagation
                    role="button"
                    tabindex="0"
                  >
                    <Dropdown>
                      <DropdownToggle
                        tag="div"
                        class="os-icon-more-horizontal"
                        data-testid="sig-card-dropdown-{signature.rulename}"
                      />
                      <DropdownMenu>
                        <DropdownItem
                          data-testid="sig-action-rename-{signature.rulename}"
                          on:click={() => onRenameSignature(signature)}>{$format('label.RENAME')}</DropdownItem
                        >
                        <DropdownItem divider />
                        <DropdownItem
                          data-testid="sig-action-edit-{signature.rulename}"
                          on:click={() => redirect(signature.rulename)}>{$format('label.EDIT')}</DropdownItem
                        >
                        <DropdownItem
                          data-testid="sig-action-clone-{signature.rulename}"
                          on:click={() => onCloneSignature(signature)}>{$format('label.CLONE')}</DropdownItem
                        >
                        {#if !signature.active}
                          <DropdownItem
                            data-testid="sig-action-archive-{signature.rulename}"
                            on:click={() => onArchiveSignature(signature)}
                          >
                            {#if !signature.archived}
                              {$format('label.ARCHIVE')}
                            {:else}
                              {$format('label.UNARCHIVE')}
                            {/if}
                          </DropdownItem>
                        {/if}
                        {#if $userStore.isStaff}
                          <DropdownItem divider />
                          <DropdownItem
                            data-testid="sig-action-clone-domain"
                            on:click={() => onCloneSignatureToDomain(signature)}
                          >
                            {$format('label.CLONE_TO_DOMAIN')}
                          </DropdownItem>
                        {/if}
                        {#if !signature.active}
                          <DropdownItem divider />
                          <DropdownItem
                            data-testid="sig-action-delete-{signature.rulename}"
                            on:click={() => onDeleteSignature(signature)}
                          >
                            {$format('label.DELETE')}
                          </DropdownItem>
                        {/if}
                      </DropdownMenu>
                    </Dropdown>
                  </div>
                </div>
                <div
                  class="signature-card--preview"
                  on:click={() => redirect(signature.rulename)}
                  role="button"
                  tabindex="0"
                >
                  {#if !signature.html}
                    {#await getUserSignature(signature)}
                      <div class="loading">
                        <Spinner show inline />
                      </div>
                    {:then html}
                      {#if signature.loading}
                        <Spinner show inline />
                      {:else}
                        <iframe
                          tabindex="-1"
                          style="width: 100%;"
                          scrolling="no"
                          title={signature.blockname}
                          srcdoc="<html><body><div style='transform: scale(.6,.6); transform-origin: left top; -moz-transform:  scale(.6,.6); -moz-transform-origin: left top;'>{html ||
                            ''}</div></body></html>"
                        />
                      {/if}
                    {:catch error}
                      <p style="color: red">{error.message}</p>
                    {/await}
                  {:else}
                    <iframe
                      tabindex="-1"
                      style="width: 100%;"
                      scrolling="no"
                      title={signature.blockname}
                      srcdoc="<html><body><div style='transform: scale(.6,.6); transform-origin: left top; -moz-transform:  scale(.6,.6); -moz-transform-origin: left top;'>{signature.html ||
                        ''}</div></body></html>"
                    />
                  {/if}
                </div>
              </div>
            {/each}
          </div>
        {:else}
          <div class="mt-3">
            <NoMatches message={$format('label.MESSAGE.NO_MATCHES_SIGNATURES')} />
          </div>
        {/if}
      {:else if initialized}
        <EmptyState
          icon="feather"
          heading={$format('label.EMPTY_STATE.SIGNATURE_HEADING')}
          description={$format('label.EMPTY_STATE.SIGNATURE_DESCRIPTION')}
          action={$format('label.EMPTY_STATE.SIGNATURE_ACTION')}
          on:click={onNewSignature}
        />
      {/if}
    </Container>
  </div>

  {#if queryResult.length}
    <div class="page-footer">
      <Pagination
        bind:currentPage={filters.paging.page}
        bind:perPage={filters.paging.perPage}
        bind:totalRecords
        {onChangeRecords}
        {onChangePage}
      />
    </div>
  {/if}
</div>

<CloneModal
  bind:show={showCloneToDomainModal}
  bind:signature={cloneSignature}
  bind:rulename={cloneSignatureRulename}
  bind:domains
  bind:updating
  domain={$userStore.domain}
/>

<RenameModal
  bind:show={showRenameModal}
  data={renameSignature}
  name={renameSignature?.rulename ?? ''}
  existingNames={queryResult.map((s) => s.rulename)}
  onUpdate={onRenameUpdate}
/>

<ChoiceModal
  bind:show={showSignatureChoiceModal}
  on:blank={() => {
    showSignatureChoiceModal = false;
    showSignatureTemplatesModal = false;
    redirect('new/blank');
  }}
  on:template={openTemplateGallery}
/>

<GalleryModal
  bind:show={showSignatureTemplatesModal}
  templates={previewTemplates}
  on:back={onGoBack}
  on:use={(e) => {
    showSignatureChoiceModal = false;
    showSignatureTemplatesModal = false;
    redirect('new/' + e.detail.id);
  }}
/>

{#if showBrandWizardModal}
  <BrandWizard bind:show={showBrandWizardModal} />
{/if}

<style lang="scss" src="./studio.scss"></style>
